import { useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';

/** خيارات عدد المنتجات (تصوير منتجات) — مفاتيح التسعير: small / medium / large / xlarge */
export const PRODUCT_COUNT_BUCKETS = [
  {
    value: 'small',
    label: 'عدد قليل',
    description: 'من 1 إلى 5 منتجات',
  },
  {
    value: 'medium',
    label: 'عدد متوسط',
    description: 'من 6 إلى 10 منتجات',
  },
  {
    value: 'large',
    label: 'عدد كبير',
    description: 'من 11 إلى 20 منتج',
  },
  {
    value: 'xlarge',
    label: 'عدد كبير جداً',
    description: 'من 21 إلى 30 منتج',
  },
];

/** تغطية الزفاف — فئة أفراد + نوع حفلات الزفاف */
export const WEDDING_COVERAGE_BUCKETS = [
  {
    value: 'wedding_event_only',
    label: 'تغطية الحفل',
    description: 'تغطية مجريات الحفل خلال وقت إقامته',
  },
  {
    value: 'wedding_prep_and_event',
    label: 'تغطية التجهيز والحفل',
    description: 'توثيق مراحل التجهيز بالإضافة إلى الحفل',
  },
  {
    value: 'wedding_full_coverage',
    label: 'تغطية شاملة',
    description: 'توثيق كامل لمراحل المناسبة من البداية حتى النهاية',
  },
];

/** التصوير الشخصي — فئة أفراد + نوع portrait */
export const PORTRAIT_SESSION_BUCKETS = [
  {
    value: 'portrait_one',
    label: 'شخص واحد',
    description: 'تصوير شخصي، بورتريه، صور سوشيال، صورة مهنية',
  },
  {
    value: 'portrait_two',
    label: 'شخصين',
    description: 'أصدقاء، شخصين معًا في نفس الجلسة',
  },
  {
    value: 'portrait_group',
    label: '3 أشخاص وأكثر',
    description: 'عائلة، مجموعة أصدقاء، عدة أشخاص',
  },
];

/** حجم المنتج الفعلي (تصوير منتجات) */
export const PRODUCT_PHYSICAL_SIZE_BUCKETS = [
  {
    value: 'compact',
    label: 'صغيرة الحجم',
    description:
      'سهلة النقل ولا تحتاج مساحة. عطور، إكسسوارات، ساعات، أدوات تجميل، جوالات، منتجات صغيرة',
  },
  {
    value: 'standard',
    label: 'متوسطة الحجم',
    description: 'سهلة الحمل وبمساحة متوسطة. ملابس، حقائب، أجهزة إلكترونية، أدوات منزلية',
  },
  {
    value: 'bulky',
    label: 'كبيرة الحجم',
    description: 'صعبة الحمل والنقل وبمساحة كبيرة. أثاث، معدات، أجهزة كبيرة، منتجات بحجم كبير',
  },
];

function getBucketsForPhotographyType(photographyType) {
  if (photographyType === 'food') {
    return [
      {
        value: 'small',
        label: 'عدد قليل — 1 إلى 5 عناصر',
        description: 'منيو صغير أو منتجات محدودة',
      },
      {
        value: 'medium',
        label: 'عدد متوسط — 6 إلى 15 عنصر',
        description: 'منيو متوسط أو مجموعة منتجات',
      },
      {
        value: 'large',
        label: 'عدد كبير — أكثر من 15 عنصر',
        description: 'منيو كامل أو قائمة كبيرة',
      },
    ];
  }
  if (photographyType === 'product') {
    return [];
  }
  if (photographyType === 'event') {
    return [
      {
        value: 'small',
        label: 'فعالية بسيطة',
        description: 'لقاء، افتتاح بسيط، حفل صغير',
      },
      {
        value: 'medium',
        label: 'فعالية منظمة',
        description: 'مؤتمر، ورشة، حفل منظم بجدول',
      },
      {
        value: 'large',
        label: 'فعالية إنتاجية',
        description: 'مؤتمر كبير، مهرجان، فعالية متعددة الأنشطة',
      },
    ];
  }
  if (photographyType === 'real-estate') {
    return [
      {
        value: 'small',
        label: 'عقار بسيط',
        description: 'شقة، محل صغير، مكتب صغير…',
      },
      {
        value: 'medium',
        label: 'عقار متوسط',
        description: 'فيلا، دوبلكس، مكتب كبير…',
      },
      {
        value: 'large',
        label: 'عقار كبير',
        description: 'قصر، مجمع، مشروع…',
      },
    ];
  }
  return [];
}

function getStepMeta(photographyType) {
  if (photographyType === 'food') {
    return {
      icon: 'UtensilsCrossed',
      title: 'ما حجم العمل المتوقع؟',
      description: 'عدد العناصر أو المواضيع التي سيتم تصويرها يؤثر على السعر',
    };
  }
  if (photographyType === 'event') {
    return {
      icon: 'Calendar',
      title: 'ما طبيعة الفعالية المراد تصويرها؟',
      description: 'نوع التصوير يؤثر على السعر النهائي وجهد المصور',
    };
  }
  if (photographyType === 'real-estate') {
    return {
      icon: 'Home',
      title: 'ما طبيعة العقار المراد تصويره؟',
      description: 'نوع التصوير يؤثر على السعر النهائي وجهد المصور',
    };
  }
  return { icon: 'Layers', title: 'تفاصيل العمل', description: '' };
}

const WorkloadBucketStep = ({ formData, onUpdate }) => {
  const photographyType = formData?.photographyType;
  const photographyCategory = formData?.photographyCategory;

  const isWeddingIndividual = photographyCategory === 'individual' && photographyType === 'wedding';
  const isPortraitIndividual = photographyCategory === 'individual' && photographyType === 'portrait';

  const workloadBuckets = useMemo(() => {
    if (photographyType === 'product') return [];
    if (isWeddingIndividual) return WEDDING_COVERAGE_BUCKETS;
    if (isPortraitIndividual) return PORTRAIT_SESSION_BUCKETS;
    return getBucketsForPhotographyType(photographyType);
  }, [photographyType, isWeddingIndividual, isPortraitIndividual]);

  const stepMeta = useMemo(() => {
    if (isWeddingIndividual) {
      return {
        icon: 'Heart',
        title: 'اختر التغطية المطلوبة',
        description: 'اختر التغطية المناسبة خلال يوم الحفل',
      };
    }
    if (isPortraitIndividual) {
      return {
        icon: 'User',
        title: 'كم عدد الأشخاص في الجلسة؟',
        description: '',
      };
    }
    return getStepMeta(photographyType);
  }, [photographyType, isWeddingIndividual, isPortraitIndividual]);

  // عند مغادرة نوع «منتجات»: مسح حقل حجم المنتج
  useEffect(() => {
    if (photographyType !== 'product' && (formData?.productPhysicalSizeBucket || formData?.productPhysicalSizeLabel)) {
      onUpdate({ productPhysicalSizeBucket: '', productPhysicalSizeLabel: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photographyType]);

  // عند تغيير نوع التصوير أو الفئة: إزالة اختيار غير صالٍ
  useEffect(() => {
    if (!photographyType) return;
    if (photographyType === 'product') {
      const validCount = new Set(PRODUCT_COUNT_BUCKETS.map((b) => b.value));
      const wb = formData?.workloadBucket;
      if (wb && !validCount.has(wb)) {
        onUpdate({ workloadBucket: '' });
      }
      return;
    }
    const buckets =
      photographyCategory === 'individual' && photographyType === 'wedding'
        ? WEDDING_COVERAGE_BUCKETS
        : photographyCategory === 'individual' && photographyType === 'portrait'
          ? PORTRAIT_SESSION_BUCKETS
          : getBucketsForPhotographyType(photographyType);
    if (buckets.length === 0) {
      if (formData?.workloadBucket) {
        onUpdate({ workloadBucket: '' });
      }
      return;
    }
    const valid = new Set(buckets.map((b) => b.value));
    const wb = formData?.workloadBucket;
    if (wb && !valid.has(wb)) {
      onUpdate({ workloadBucket: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photographyType, photographyCategory, formData?.workloadBucket]);

  const handleBucketChange = (value) => {
    onUpdate({ workloadBucket: value });
  };

  const handleProductSizeChange = (value) => {
    const bucket = PRODUCT_PHYSICAL_SIZE_BUCKETS.find((b) => b.value === value);
    onUpdate({
      productPhysicalSizeBucket: value,
      productPhysicalSizeLabel: bucket ? `${bucket.label} — ${bucket.description}` : value,
    });
  };

  if (!photographyType) {
    return null;
  }

  if (photographyType === 'product') {
    return (
      <div className="space-y-6 w-full">
        <QuestionCard
          icon="Package"
          title="كم عدد المنتجات التي سيتم تصويرها؟"
          description="اختر عدد المنتجات الدقيق وليس عدد الصور"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PRODUCT_COUNT_BUCKETS.map((bucket) => (
              <button
                key={bucket.value}
                type="button"
                onClick={() => handleBucketChange(bucket.value)}
                className={`p-5 rounded-xl border-2 text-right transition-all hover:border-primary hover:bg-primary/5 ${
                  formData?.workloadBucket === bucket.value
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-border bg-background'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-base font-semibold text-foreground mb-1">{bucket.label}</div>
                    <div className="text-sm text-muted-foreground">{bucket.description}</div>
                  </div>
                  {formData?.workloadBucket === bucket.value && (
                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </QuestionCard>

        <QuestionCard icon="Box" title="ما هو حجم المنتجات؟" description="">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PRODUCT_PHYSICAL_SIZE_BUCKETS.map((bucket) => (
              <button
                key={bucket.value}
                type="button"
                onClick={() => handleProductSizeChange(bucket.value)}
                className={`p-5 rounded-xl border-2 text-right transition-all hover:border-primary hover:bg-primary/5 ${
                  formData?.productPhysicalSizeBucket === bucket.value
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-border bg-background'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-base font-semibold text-foreground mb-1">{bucket.label}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed">{bucket.description}</div>
                  </div>
                  {formData?.productPhysicalSizeBucket === bucket.value && (
                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </QuestionCard>
      </div>
    );
  }

  if (workloadBuckets.length === 0) {
    return null;
  }

  return (
    <QuestionCard icon={stepMeta.icon} title={stepMeta.title} description={stepMeta.description}>
      <div
        className={
          isWeddingIndividual || isPortraitIndividual
            ? 'grid grid-cols-1 md:grid-cols-3 gap-3'
            : 'grid grid-cols-1 md:grid-cols-2 gap-3'
        }
      >
        {workloadBuckets.map((bucket) => (
          <button
            key={bucket.value}
            type="button"
            onClick={() => handleBucketChange(bucket.value)}
            className={`p-5 rounded-xl border-2 text-right transition-all hover:border-primary hover:bg-primary/5 ${
              formData?.workloadBucket === bucket.value
                ? 'border-primary bg-primary/10 shadow-sm'
                : 'border-border bg-background'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-base font-semibold text-foreground mb-1">{bucket.label}</div>
                <div className="text-sm text-muted-foreground">{bucket.description}</div>
              </div>
              {formData?.workloadBucket === bucket.value && (
                <Icon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    </QuestionCard>
  );
};

export default WorkloadBucketStep;
